import { useCallback } from "react";

import { useFireAnalyticsEvents } from "@/components/fastshift/analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey
} from "@/components/fastshift/analytics/constants";

const TABLE_EVENT_TYPE = {
    SEARCH: "search",
    PAGE_SIZE: "pageSize",
    SORT: "sort",
    NAVIGATION: "navigation"
} as const;

type TableEventType = (typeof TABLE_EVENT_TYPE)[keyof typeof TABLE_EVENT_TYPE];

const getActionSubjectIdBySource = (
    source: SourceKey,
    eventType: TableEventType
): ActionSubjectIdKey | undefined => {
    const mapping: Partial<
        Record<SourceKey, Partial<Record<TableEventType, ActionSubjectIdKey>>>
    > = {
        [SourceKey.FASTSHIFT_PORTAL_SELECTION_SCREEN]: {
            [TABLE_EVENT_TYPE.SEARCH]:
                ActionSubjectIdKey.DOMAIN_SELECTION_TABLE_SEARCH,
            [TABLE_EVENT_TYPE.PAGE_SIZE]:
                ActionSubjectIdKey.SELECTION_TABLE_CHANGE_PAGE_SIZE_DROPDOWN_ITEM,
            [TABLE_EVENT_TYPE.SORT]:
                ActionSubjectIdKey.SELECTION_TABLE_COLUMN_SORT,
            [TABLE_EVENT_TYPE.NAVIGATION]:
                ActionSubjectIdKey.SELECTION_TABLE_PAGE_NAVIGATION_BUTTON
        },
        [SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN]: {
            [TABLE_EVENT_TYPE.PAGE_SIZE]:
                ActionSubjectIdKey.FASTSHIFT_MIGRATION_PROGRESS_TABLE_CHANGE_PAGE_SIZE_DROPDOWN_ITEM,
            [TABLE_EVENT_TYPE.NAVIGATION]:
                ActionSubjectIdKey.FASTSHIFT_MIGRATION_PROGRESS_TABLE_PAGE_NAVIGATION_BUTTON
        }
    };

    return mapping[source]?.[eventType];
};

type IdentityAttrs = {
    role: string;
    userDomain: string;
};

export const useFastshiftTableAnalytics = (
    identityAttrs?: IdentityAttrs | null,
    source?: SourceKey
) => {
    const { fireUIEvent } = useFireAnalyticsEvents(identityAttrs);

    const sendSearchUIEvent = useCallback(() => {
        if (!identityAttrs || !source) return;
        const actionSubjectId = getActionSubjectIdBySource(
            source,
            TABLE_EVENT_TYPE.SEARCH
        );
        if (!actionSubjectId) return;
        fireUIEvent({
            action: ActionKey.SUBMITTED,
            actionSubject: ActionSubjectKey.SEARCH,
            actionSubjectId,
            source
        });
    }, [fireUIEvent, source, identityAttrs]);

    const sendNavigationUIEvent = useCallback(
        (buttonType: string, totalPages: number) => {
            if (!identityAttrs || !source) return;
            const actionSubjectId = getActionSubjectIdBySource(
                source,
                TABLE_EVENT_TYPE.NAVIGATION
            );
            if (!actionSubjectId) return;
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.BUTTON,
                actionSubjectId,
                source,
                attributes: {
                    buttonType,
                    totalPages
                }
            });
        },
        [fireUIEvent, source, identityAttrs]
    );

    const sendPageSizeChangeUIEvent = useCallback(
        (newPageSize: number) => {
            if (!identityAttrs || !source) return;
            const actionSubjectId = getActionSubjectIdBySource(
                source,
                TABLE_EVENT_TYPE.PAGE_SIZE
            );
            if (!actionSubjectId) return;
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
                actionSubjectId,
                source,
                attributes: {
                    newPageSize
                }
            });
        },
        [fireUIEvent, source, identityAttrs]
    );

    const sendSortUIEvent = useCallback(
        (column: string, direction: string) => {
            if (!identityAttrs || !source) return;
            const actionSubjectId = getActionSubjectIdBySource(
                source,
                TABLE_EVENT_TYPE.SORT
            );
            if (!actionSubjectId) return;
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.TABLE_COLUMN_SORT,
                actionSubjectId,
                source,
                attributes: {
                    columnTitle: column,
                    sortType: direction
                }
            });
        },
        [fireUIEvent, source, identityAttrs]
    );

    return {
        sendSearchUIEvent,
        sendNavigationUIEvent,
        sendPageSizeChangeUIEvent,
        sendSortUIEvent
    };
};
